/* eslint-disable max-lines */
import { useEffect, useRef, useState } from 'react';
import {
  Outlet, useLocation, useNavigate, useParams, 
} from 'react-router-dom';
import { io } from 'socket.io-client';
import { v4 as uuidv4 } from 'uuid';

import InactivityModal from '@App/components/InactivityModal/InactivityModal';

import './tablet-layout.css';

const socket = io({ autoConnect: false });

const TABLET_IDS = [1, 2];
const QUESTION_ROUTES = ['question1', 'question2', 'question3', 'question4', 'question5'];
const COMPLETED_QUESTIONS_KEY = 'tabletCompletedQuestions';
const INTRODUCTION_COMPLETE_KEY = 'tabletIntroductionComplete';
const isDevelopment = import.meta.env.VITE_NAZKA_ENV === 'development';

function isValidTabletId(tabletId) {
  return TABLET_IDS.includes(Number(tabletId));
}

function getTabletRoute(pathname) {
  const route = pathname.split('/').filter(Boolean).pop();

  return QUESTION_ROUTES.includes(route) ? route : 'waiting';
}

function readCompletedQuestions() {
  try {
    const storedQuestions = JSON.parse(sessionStorage.getItem(COMPLETED_QUESTIONS_KEY));

    return Array.isArray(storedQuestions) ? storedQuestions : [];
  } catch {
    return [];
  }
}

function getQuestionNumber(pathname) {
  const match = pathname.match(/\/question([1-5])\/?$/);

  return match ? Number(match[1]) : null;
}

function TabletLayout() {
  const participantStartedRef = useRef(false);
  const [lastAnswer, setLastAnswer] = useState(null);
  const { tabletId } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isIntroductionRoute = new RegExp(`^/tablet/${tabletId}/?$`).test(pathname);
  const questionNumber = getQuestionNumber(pathname);
  const [participantId, setParticipantId] = useState(
    () => (isIntroductionRoute ? null : sessionStorage.getItem('tabletParticipantId')),
  );
  const [completedQuestions, setCompletedQuestions] = useState(
    () => (isIntroductionRoute ? [] : readCompletedQuestions()),
  );
  const hasIntroduction = Boolean(
    sessionStorage.getItem('tabletParticipantId')
      && sessionStorage.getItem(INTRODUCTION_COMPLETE_KEY) === 'true',
  );
  const previousQuestion = questionNumber > 1 ? `question${questionNumber - 1}` : null;
  const hasPreviousAnswer = !previousQuestion || completedQuestions.includes(previousQuestion);
  const shouldRedirect = !isIntroductionRoute && (!hasIntroduction || !hasPreviousAnswer);

  function startParticipant(introduction) {
    const nextParticipantId = uuidv4();

    participantStartedRef.current = true;
    sessionStorage.setItem('tabletParticipantId', nextParticipantId);
    sessionStorage.setItem('tabletResidence', introduction.residence);
    sessionStorage.setItem(
      'tabletResidenceCoordinates',
      JSON.stringify(introduction.residenceCoordinates),
    );
    sessionStorage.setItem(INTRODUCTION_COMPLETE_KEY, 'true');
    sessionStorage.removeItem(COMPLETED_QUESTIONS_KEY);
    setParticipantId(nextParticipantId);
    setCompletedQuestions([]);
    setLastAnswer(null);
    socket.emit('start_participant', {
      participantId: nextParticipantId,
      introduction,
      tabletId: Number(tabletId),
    });
  }

  useEffect(() => {
    if (!isIntroductionRoute) {
      participantStartedRef.current = false;
      return;
    }
    if (participantStartedRef.current) return;

    sessionStorage.removeItem('tabletParticipantId');
    sessionStorage.removeItem('tabletResidence');
    sessionStorage.removeItem('tabletResidenceCoordinates');
    sessionStorage.removeItem(COMPLETED_QUESTIONS_KEY);
    sessionStorage.removeItem(INTRODUCTION_COMPLETE_KEY);
    setParticipantId(null);
    setCompletedQuestions([]);
    setLastAnswer(null);
  }, [isIntroductionRoute]);

  useEffect(() => {
    if (!isIntroductionRoute && shouldRedirect) {
      navigate(`/tablet/${tabletId}`, { replace: true });
    }
  }, [isIntroductionRoute, navigate, shouldRedirect, tabletId]);

  useEffect(() => {
    if (!isValidTabletId(tabletId)) {
      navigate('/tablet/1');
    }
  }, [navigate, tabletId]);

  useEffect(() => {
    socket.connect();

    socket.on('connect', () => {
      if (isValidTabletId(tabletId)) {
        socket.emit('register', `tablet-${tabletId}`);
      }
    });

    socket.on('new_answer', (questionId, answer, id) => {
      setLastAnswer({ questionId, answer, tabletId: id });
    });

    return () => {
      socket.off('connect');
      socket.off('new_answer');
      socket.disconnect();
    };
  }, [navigate, tabletId]);

  useEffect(() => {
    const emitRoute = () => {
      if (isValidTabletId(tabletId)) {
        const storedCoordinates = sessionStorage.getItem('tabletResidenceCoordinates');
        const lang = document.documentElement.getAttribute('lang') || 'nl';

        socket.emit('tablet_route', {
          tabletId: Number(tabletId),
          route: getTabletRoute(pathname),
          lang,
          participantId: sessionStorage.getItem('tabletParticipantId'),
          residenceCoordinates: storedCoordinates ? JSON.parse(storedCoordinates) : null,
        });
      }
    };

    if (socket.connected) {
      emitRoute();
      return undefined;
    }

    socket.once('connect', emitRoute);

    return () => socket.off('connect', emitRoute);
  }, [pathname, tabletId]);

  if (shouldRedirect) return null;

  return (
    <div
      className={`tablet-layout tablet-layout--tablet-${tabletId} ${isDevelopment ? 'tablet-layout--development-preview' : ''}`.trim()}
    >
      <div className="tablet-layout__stage">
        <Outlet
          context={{
            socket,
            onStartParticipant: startParticipant,
            onSelectAnswer: (questionId, answer) => {
              const activeParticipantId = participantId || sessionStorage.getItem('tabletParticipantId');

              if (!activeParticipantId) {
                navigate(`/tablet/${tabletId}`, { replace: true });
                return;
              }

              const lang = document.documentElement.getAttribute('lang') || 'nl';

              setCompletedQuestions((currentQuestions) => {
                const nextQuestions = currentQuestions.includes(questionId)
                  ? currentQuestions
                  : [...currentQuestions, questionId];

                sessionStorage.setItem(COMPLETED_QUESTIONS_KEY, JSON.stringify(nextQuestions));
                return nextQuestions;
              });

              socket.emit('answer', {
                participantId,
                questionId,
                answer,
                lang,
                tabletId: Number(tabletId),
              });
            },
            participantId,
            lastAnswer,
          }}
        />
        <InactivityModal />
      </div>
    </div>
  )
}

export default TabletLayout;